import { createContext, useContext, useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { isSentryEnabled, captureError } from '../lib/sentry';
import { authService } from '../services/authService';

const AuthContext = createContext(null);

/**
 * Attach user identity to Sentry so events are linked to the right person.
 *
 * Per umbrella plan 02 § C-6 / hard-constraint #6: email is NEVER sent.
 * Identity is `{ id, farm_id }` only — no PII.
 */
function setSentryUser(user) {
  if (!isSentryEnabled) return;
  if (user) {
    Sentry.setUser({
      id: user._id ?? user.id,
      farm_id: user.farm,
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Filter helper for session-restore errors — suppresses expected expired-
 * session / unauthorized signals (the happy path on revisit after JWT lapse)
 * while letting genuine failures (network, 5xx, parse errors) page.
 *
 * Never throws.
 */
function isExpiredOr401(err) {
  if (!err) return true;
  if (err?.response?.status === 401) return true;
  if (err?.code === 'TOKEN_EXPIRED') return true;
  const msg = String(err?.message ?? '').toLowerCase();
  return msg.includes('expired') || msg.includes('unauthorized');
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Validate session with backend on mount
    authService
      .checkSession()
      .then((serverUser) => {
        setUser(serverUser);
        setSentryUser(serverUser);
      })
      .catch((err) => {
        // Session invalid or expired — clear local state
        localStorage.removeItem('user');
        setUser(null);
        setSentryUser(null);

        // Report only unexpected failures (network, 5xx, parse errors).
        // Expected expired-session / 401 signals are the happy path on
        // revisit after JWT lapse and would spam Sentry on every user
        // revisit if captured.
        if (!isExpiredOr401(err)) {
          captureError(err, {
            area: 'auth',
            flow: 'session_restore_failed',
            level: 'warning',
          });
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { user: loggedInUser } = await authService.login(email, password);
    setUser(loggedInUser);
    setSentryUser(loggedInUser);
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
    setSentryUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      loading
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
